import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import Header from "../components/Header";
import SubscriptionCard from "../components/subscription/SubscriptionCard";
import { getCookies,removeCookie } from "../controllers/cookies.controller";
import globals from "../globals";

import fxRightImg from "../img/fx-right.svg";
import fxLeftImg from "../img/fx-left.svg";

import "../styles/subscription.css";

interface IUser {
    nome: string;
    email: string;
    cpf_cnpj: string;
    telefone: string;
}

interface IRecebimento {
    id: number;
    valor: string;
    vencimento: string;
    status: 'R' | 'A'; // R = Recebido, A = Aguardando
    descricao: string;
    tipo_recebimento: 'Transferencia' | 'Gateway' | 'Boleto' | 'Cartão' | 'PIX';
}

interface IESim {
    qrcode?: string;
    qrcode_base64?: string;
    sm_dp_address?: string;
    activation_code?: string;
}

interface IPortabilidadeStatus {
    status?: string;
    protocol_id?: string;
    phone_number_ported?: string;
    phone_number_temporary?: string;
    customer_name?: string;
    created_date?: string;
    scheduled_date?: string;
    authorized?: boolean;
    donor_provider?: string;
    recipient_provider?: string;
}

interface IPortabilidade {
    ativa?: boolean;
    numero_origem?: string;
    data_agendamento?: string;
    gateway_id?: string;
    status_api?: IPortabilidadeStatus;
}

interface IMvnoData {
    status: false | 'PENDENTE' | 'CONFIGURANDO' | 'AGUARDANDO_ATIVACAO' | 'AGUARDANDO_PORTABILIDADE' | 'CONFIGURANDO_PORTABILIDADE' | 'ATIVO' | 'SUSPENSO' | 'CANCELADO';
    chip_type?: 'ESIM' | 'FISICO';
    esim?: IESim | null;
    iccid?: string | null;
    linha_ativa: boolean | string;
    phone_number?: string | null;
    portabilidade?: IPortabilidade | null;
}

interface ICancelamentoAgendado {
    id: number;
    data_cancelamento: string;
    motivo: string;
    cancelar_internet: boolean;
    cancelar_tv: boolean;
    cancelar_mvno: boolean;
    status: 'PENDENTE' | 'PROCESSADO' | 'CANCELADO';
}

interface IAssinatura {
    id: number;
    contrato: string;
    data_ativacao: string;
    data_expiracao: string;
    data_pagamento: string;
    valor_plano: string | null;
    status: string;
    a_receber: IRecebimento[];
    tipo_plano: 'TELEFONIA_MOVEL' | 'STREAMING' | 'INTERNET' | 'outros';
    mvno?: IMvnoData;
    cancelamento_agendado?: ICancelamentoAgendado | null;
    status_assinatura?: 'PENDENTE' | 'CONFIGURANDO' | 'AGUARDANDO_ATIVACAO' | 'ATIVO' | 'SUSPENSO' | 'CANCELADO' | null;
}

const Subscription = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState<IUser | null>(null);
    const [assinaturas, setAssinaturas] = useState<IAssinatura[]>([]);
    const [loading, setLoading] = useState(true);
    const [activatingMvno, setActivatingMvno] = useState<number | null>(null);
    const [showQRCode, setShowQRCode] = useState<Record<number, boolean>>({});
    const [selectedAreaCode, setSelectedAreaCode] = useState<Record<number, string>>({});
    const [selectedChipType, setSelectedChipType] = useState<Record<number, 'ESIM' | 'FISICO' | ''>>({});
    const [iccidInput, setIccidInput] = useState<Record<number, string>>({});
    const [cancellingContract, setCancellingContract] = useState<number | null>(null);
    const [showCancelModal, setShowCancelModal] = useState<number | null>(null);
    const [cancelReason, setCancelReason] = useState<string>('');
    const [cancelOptions, setCancelOptions] = useState<Record<number, { internet: boolean; tv: boolean; mvno: boolean }>>({});

    const [configurandoPortabilidade, setConfigurandoPortabilidade] = useState<number | null>(null);

    // Estados para Portabilidade
    const [portabilidadeData, setPortabilidadeData] = useState<Record<number, { ativar: boolean; numeroAtual: string; dataAgendamento: string; mudancaTitular: boolean; novoTitular: string; cpfNovoTitular: string }>>({});
    const [portabilidadeDatas, setPortabilidadeDatas] = useState<Record<number, Array<{ start: string; end: string }>>>({});
    const [loadingPortabilidadeDatas, setLoadingPortabilidadeDatas] = useState<Record<number, boolean>>({});

    // Função para formatar CPF/CNPJ
    const formatarDocumento = (documento: string): string => {
        const cleaned = documento.replace(/\D/g, '');
        if (cleaned.length === 11) {
            return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        }
        if (cleaned.length === 14) {
            return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }
        return documento;
    };

    // Função para formatar número de telefone
    const formatarTelefone = (numero: string): string => {
        const cleaned = numero.replace(/\D/g, '');
        if (cleaned.length === 11) {
            return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        }
        return numero;
    };

    useEffect(() => {
        const token = getCookies('token');
        // Token já validado pelo ProtectedRoute
        if (token) {
            loadUserData(token);
        } else {
            navigate('/');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Inicializar DDD automaticamente quando usuário e assinaturas forem carregados
    useEffect(() => {
        if (user?.telefone && assinaturas.length > 0) {
            const defaultDDD = user.telefone.substring(1, 3); // Pega os 2 dígitos após o primeiro caractere
            const newSelectedAreaCodes: { [key: string]: string } = {};

            assinaturas.forEach(assinatura => {
                // Preencher DDD para assinaturas MVNO que ainda não foram configuradas (status === false)
                if (assinatura.tipo_plano === 'TELEFONIA_MOVEL' && assinatura.mvno?.status === false) {
                    newSelectedAreaCodes[assinatura.id] = defaultDDD;
                }
            });

            if (Object.keys(newSelectedAreaCodes).length > 0) {
                setSelectedAreaCode(newSelectedAreaCodes);
            }
        }
    }, [user, assinaturas]);

    const loadUserData = async (token: string) => {
        try {
            // Buscar dados do usuário
            const userResponse = await fetch(`${globals.apiBaseUrl}/api/contratoonline/user`, {
                method: 'GET',
                headers: {
                    'Authorization': token
                }
            });

            const userData = await userResponse.json();

            if (userData.status === 'sucess') {
                setUser(userData.user);
            } else {
                throw new Error('Falha ao carregar dados do usuário');
            }

            // Buscar assinaturas
            const assinaturasResponse = await fetch(`${globals.apiBaseUrl}/api/contratoonline/assinaturas`, {
                method: 'GET',
                headers: {
                    'Authorization': token
                }
            });

            const assinaturasData = await assinaturasResponse.json();

            if (assinaturasData.status === 'success') {
                setAssinaturas(assinaturasData.data);
            } else {
                console.error('Falha ao carregar assinaturas:', assinaturasData);
            }
        } catch (error) {
            console.error("Erro ao carregar dados:", error);
            removeCookie('token');
            navigate('/login');
        } finally {
            setLoading(false);
        }
    };

    const ativarMVNO = async (contratoId: number, chipType: 'ESIM' | 'FISICO', iccid?: string) => {

        const token = getCookies('token');
        if (!token) return;

        const areaCode = selectedAreaCode[contratoId];
        if (!areaCode) {
            alert('Por favor, selecione o código de área (DDD)');
            return;
        }

        // if (chipType === 'FISICO' && !iccid) {
        //     alert('Por favor, informe o ICCID do chip físico');
        //     return;
        // }

        setActivatingMvno(contratoId);

        try {
            const body: any = {
                contrato_id: contratoId,
                chip_type: chipType,
                area_code: areaCode
            };

            // Adicionar ICCID se for chip físico
            // if (chipType === 'FISICO' && iccid) {
            //     body.iccid = iccid;
            // }

            // Adicionar dados de portabilidade se ativada
            const portData = portabilidadeData[contratoId];
            if (portData?.ativar) {
                body.portabilidade = true;
                body.numero_origem = portData.numeroAtual.replace(/\D/g, ''); // Remove formatação
                body.data_agendamento_portabilidade = portData.dataAgendamento;
                body.portabilidade_ownership = portData.mudancaTitular;

                if (portData.mudancaTitular) {
                    body.owner_name = portData.novoTitular;
                    body.owner_document = portData.cpfNovoTitular.replace(/\D/g, ''); // Remove formatação
                }
            }

            const response = await fetch(`${globals.apiBaseUrl}/api/contratoonline/mvno/config`, {
                method: 'POST',
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            const data = await response.json();

            if (data.status === 'success' && data.assinatura) {
                // Atualizar assinatura com os dados retornados pela API
                setAssinaturas(prev => prev.map(assinatura =>
                    assinatura.id === contratoId
                        ? {
                            ...assinatura,
                            mvno: {
                                status: data.assinatura.status as IMvnoData['status'],
                                chip_type: data.assinatura.chip_type || chipType,
                                esim: data.assinatura.esim,
                                linha_ativa: true,
                                portabilidade: data.portabilidade || undefined
                            }
                        }
                        : assinatura
                ));

                // Mostrar QR code se for e-SIM e tiver dados do e-SIM
                if (chipType === 'ESIM' && data.assinatura.esim) {
                    setShowQRCode(prev => ({ ...prev, [contratoId]: true }));
                }

                // Limpar dados de portabilidade após sucesso
                setPortabilidadeData(prev => {
                    const newData = { ...prev };
                    delete newData[contratoId];
                    return newData;
                });

                alert(data.msg || 'Linha MVNO ativada com sucesso!');
            } else {
                alert(data.msg || data.message || 'Erro ao ativar linha MVNO');
            }
        } catch (error) {
            console.error('Erro ao ativar MVNO:', error);
            alert('Erro ao ativar linha MVNO');
        } finally {
            setActivatingMvno(null);
        }
    };

    const toggleQRCode = (contratoId: number) => {
        setShowQRCode(prev => ({ ...prev, [contratoId]: !prev[contratoId] }));
    };

    const carregarDatasPortabilidade = async (contratoId: number) => {
        const token = getCookies('token');
        if (!token) return;

        setLoadingPortabilidadeDatas(prev => ({ ...prev, [contratoId]: true }));

        try {
            const response = await fetch(`${globals.apiBaseUrl}/api/contratoonline/mvno/portabilidade/datas`, {
                method: 'GET',
                headers: {
                    'Authorization': token
                }
            });

            const data = await response.json();

            if (data.success && data.data && Array.isArray(data.data)) {
                setPortabilidadeDatas(prev => ({ ...prev, [contratoId]: data.data }));
            } else {
                console.warn('Nenhuma data de portabilidade disponível:', data.message);
                setPortabilidadeDatas(prev => ({ ...prev, [contratoId]: [] }));
                alert(data.message || 'Nenhuma janela de portabilidade disponível no momento');
            }
        } catch (error) {
            console.error('Erro ao carregar datas de portabilidade:', error);
            alert('Erro ao carregar datas de portabilidade disponíveis');
        } finally {
            setLoadingPortabilidadeDatas(prev => ({ ...prev, [contratoId]: false }));
        }
    };

    const handleAtivarPortabilidade = (contratoId: number) => {
        const portData = portabilidadeData[contratoId];

        // Se já tinha dados, carrega as datas novamente
        if (portData?.ativar) {
            carregarDatasPortabilidade(contratoId);
        }
    };

    const configurarPortabilidade = async (contratoId: number) => {
        const token = getCookies('token');
        if (!token) return;

        const portData = portabilidadeData[contratoId];
        if (!portData?.numeroAtual) {
            alert('Por favor, informe o número atual com DDD');
            return;
        }
        if (!portData.dataAgendamento) {
            alert('Por favor, selecione uma data de agendamento');
            return;
        }

        setConfigurandoPortabilidade(contratoId);

        try {
            const numeroDigits = portData.numeroAtual.replace(/\D/g, '');
            const body: any = {
                contrato_id: contratoId,
                numero_origem: numeroDigits,
                data_agendamento: portData.dataAgendamento
            };

            if (portData.mudancaTitular) {
                body.portabilidade_ownership = true;
                body.owner_name = portData.novoTitular;
                body.owner_document = portData.cpfNovoTitular.replace(/\D/g, '');
            }

            const response = await fetch(`${globals.apiBaseUrl}/api/contratoonline/mvno/portabilidade/config`, {
                method: 'POST',
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            const data = await response.json();

            if (data.status === 'success') {
                setAssinaturas(prev => prev.map(assinatura =>
                    assinatura.id === contratoId
                        ? {
                            ...assinatura,
                            mvno: {
                                ...assinatura.mvno!,
                                status: 'AGUARDANDO_PORTABILIDADE',
                                portabilidade: data.portabilidade || assinatura.mvno?.portabilidade
                            }
                        }
                        : assinatura
                ));

                setPortabilidadeData(prev => {
                    const newData = { ...prev };
                    delete newData[contratoId];
                    return newData;
                });

                alert(data.msg || 'Portabilidade solicitada com sucesso!');
            } else {
                alert(data.msg || data.message || 'Erro ao solicitar portabilidade');
            }
        } catch (error) {
            console.error('Erro ao configurar portabilidade:', error);
            alert('Erro ao solicitar portabilidade');
        } finally {
            setConfigurandoPortabilidade(null);
        }
    };

    const handleCancelContract = async (contratoId: number) => {
        const token = getCookies('token');
        if (!token) return;

        const options = cancelOptions[contratoId] || { internet: true, tv: true, mvno: true };

        if (!window.confirm('Tem certeza que deseja solicitar o cancelamento desta assinatura? O cancelamento será processado no próximo vencimento.')) {
            return;
        }

        setCancellingContract(contratoId);

        try {
            const response = await fetch(`${globals.apiBaseUrl}/api/contratoonline/cancelamento`, {
                method: 'POST',
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id_contrato: contratoId,
                    // cancelar_internet: options.internet,
                    // cancelar_tv: options.tv,
                    // cancelar_mvno: options.mvno,
                    motivo: cancelReason || 'Cliente solicitou cancelamento'
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                alert(`Cancelamento agendado com sucesso!\nData de cancelamento: ${data.data_cancelamento}`);

                // Atualizar a assinatura com os dados de cancelamento
                setAssinaturas(prev => prev.map(assinatura =>
                    assinatura.id === contratoId
                        ? {
                            ...assinatura,
                            cancelamento_agendado: {
                                id: data.cancelamento_id,
                                data_cancelamento: data.data_cancelamento,
                                motivo: data.motivo,
                                cancelar_internet: data.servicos_agendados.internet,
                                cancelar_tv: data.servicos_agendados.tv,
                                cancelar_mvno: data.servicos_agendados.mvno,
                                status: 'PENDENTE'
                            }
                        }
                        : assinatura
                ));

                setShowCancelModal(null);
                setCancelReason('');
            } else {
                alert(data.msg || 'Erro ao agendar cancelamento');
            }
        } catch (error) {
            console.error('Erro ao cancelar contrato:', error);
            alert('Erro ao agendar cancelamento');
        } finally {
            setCancellingContract(null);
        }
    };

    const openCancelModal = (contratoId: number) => {
        setShowCancelModal(contratoId);
        setCancelOptions(prev => ({
            ...prev,
            [contratoId]: { internet: true, tv: true, mvno: true }
        }));
    };

    const handlePagamento = (id: number, contratoId: number, valor: string) => {
        navigate('/pagamento', { state: { id, contrato_id: contratoId, valor } });
    };

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="subscription-loading">
                    <div className="subscription-loading-spinner" />
                    <span className="subscription-loading-text">Carregando suas informações...</span>
                </div>
            </React.Fragment>
        );
    }

    if (!user) {
        return (
            <React.Fragment>
                <Header />
                <div className="subscription-loading">
                    <span className="subscription-loading-text">Não foi possível carregar seus dados.</span>
                </div>
            </React.Fragment>
        );
    }

    const userInitials = user.nome
        .split(' ')
        .slice(0, 2)
        .map(n => n[0])
        .join('')
        .toUpperCase();

    return (
        <React.Fragment>
            <Header />
            <div className="subscription-container">

                {/* Profile Card */}
                <div className="sub-profile-card">
                    <div className="sub-avatar">{userInitials}</div>
                    <div className="sub-profile-info">
                        <p className="sub-profile-name">{user.nome}</p>
                        <p className="sub-profile-email">{user.email}</p>
                        <div className="sub-profile-tags">
                            <span className="sub-profile-tag">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" fill="currentColor"/></svg>
                                {user.cpf_cnpj}
                            </span>
                            <span className="sub-profile-tag">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" fill="currentColor"/></svg>
                                {user.telefone}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Section Header */}
                <div className="sub-section-header">
                    <h2 className="sub-section-title">Minhas Assinaturas</h2>
                    <button className="sub-btn-new" onClick={() => navigate('/selecione-plano')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>
                        Nova
                    </button>
                </div>

                {/* Subscription Cards */}
                {assinaturas.length === 0 ? (
                    <div className="sub-empty">
                        <span className="sub-empty-icon">📋</span>
                        <p className="sub-empty-text">Você ainda não possui assinaturas ativas.</p>
                    </div>
                ) : (
                    assinaturas.map((assinatura) => (
                        <SubscriptionCard
                            key={assinatura.id}
                            assinatura={assinatura}
                            showQRCode={showQRCode[assinatura.id] || false}
                            selectedAreaCode={selectedAreaCode[assinatura.id] || ''}
                            activatingMvno={activatingMvno === assinatura.id}
                            portabilidade={portabilidadeData[assinatura.id]}
                            portabilidadeDatas={portabilidadeDatas[assinatura.id]}
                            loadingPortabilidadeDatas={loadingPortabilidadeDatas[assinatura.id] || false}
                            onCancelClick={() => openCancelModal(assinatura.id)}
                            onToggleQRCode={() => toggleQRCode(assinatura.id)}
                            onAreaCodeChange={(code) => setSelectedAreaCode(prev => ({ ...prev, [assinatura.id]: code }))}
                            onAtivarMVNO={(chipType) => ativarMVNO(assinatura.id, chipType, iccidInput[assinatura.id])}
                            onPagar={(id, contratoId, valor) => handlePagamento(id, contratoId, valor)}
                            onPortabilidadeChange={(data) => setPortabilidadeData(prev => {
                                const current = prev[assinatura.id] ?? {
                                    ativar: false,
                                    numeroAtual: '',
                                    dataAgendamento: '',
                                    mudancaTitular: false,
                                    novoTitular: '',
                                    cpfNovoTitular: ''
                                };
                                return { ...prev, [assinatura.id]: { ...current, ...data } };
                            })}
                            onCarregarDatasPortabilidade={() => carregarDatasPortabilidade(assinatura.id)}
                            onConfigurarPortabilidade={() => configurarPortabilidade(assinatura.id)}
                            configurandoPortabilidade={configurandoPortabilidade === assinatura.id}
                        />
                    ))
                )}

                {/* Faturas Link */}
                <a href="/minhas-faturas" className="card-link">
                    <div>
                        <b>Últimas faturas</b>
                        <span>Ver histórico de pagamento</span>
                    </div>
                    <svg width="44" height="44" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M46.4508 79.3999L65.8508 59.9999L46.4508 40.5999C44.5008 38.6499 44.5008 35.4999 46.4508 33.5499C48.4008 31.5999 51.5508 31.5999 53.5008 33.5499L76.4508 56.4999C78.4008 58.4499 78.4008 61.5999 76.4508 63.5499L53.5008 86.4999C51.5508 88.4499 48.4008 88.4499 46.4508 86.4999C44.5508 84.5499 44.5008 81.3499 46.4508 79.3999Z" fill="white" />
                    </svg>
                </a>
            </div>

            {/* Cancel Modal */}
            {showCancelModal !== null && (
                <div className="cancel-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setShowCancelModal(null); setCancelReason(''); } }}>
                    <div className="cancel-modal">
                        <div className="cancel-modal-handle" />
                        <div className="cancel-modal-header">
                            <h3 className="cancel-modal-title">Solicitar Cancelamento</h3>
                            <p className="cancel-modal-subtitle">
                                O cancelamento será processado no próximo vencimento. Escolha quais serviços deseja cancelar:
                            </p>
                        </div>

                        <div className="cancel-modal-body">
                           
                            <div>
                                <span className="cancel-reason-label">Motivo do cancelamento</span>
                                <textarea
                                    className="cancel-reason-textarea"
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(e.target.value)}
                                    placeholder="Digite o motivo do cancelamento..."
                                />
                            </div>
                        </div>

                        <div className="cancel-modal-footer">
                            <button
                                className="cancel-btn-dismiss"
                                onClick={() => { setShowCancelModal(null); setCancelReason(''); }}
                            >
                                Voltar
                            </button>
                            <button
                                className="cancel-btn-confirm"
                                onClick={() => handleCancelContract(showCancelModal)}
                                disabled={cancellingContract === showCancelModal}
                            >
                                {cancellingContract === showCancelModal ? 'Processando...' : 'Confirmar Cancelamento'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="security">
                <svg height="14" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.3934 0H1.85395C1.36225 0 0.890691 0.195326 0.543008 0.543008C0.195326 0.890691 0 1.36225 0 1.85395V8.66372C0 19.047 8.78539 22.4918 10.5443 23.077C10.92 23.2048 11.3274 23.2048 11.703 23.077C13.4643 22.4918 22.2474 19.047 22.2474 8.66372V1.85395C22.2474 1.36225 22.052 0.890691 21.7044 0.543008C21.3567 0.195326 20.8851 0 20.3934 0ZM16.4144 8.07162L9.92557 14.5604C9.83948 14.6466 9.73724 14.715 9.62471 14.7616C9.51218 14.8083 9.39155 14.8323 9.26974 14.8323C9.14792 14.8323 9.02729 14.8083 8.91476 14.7616C8.80223 14.715 8.69999 14.6466 8.6139 14.5604L5.83298 11.7795C5.65904 11.6056 5.56133 11.3697 5.56133 11.1237C5.56133 10.8777 5.65904 10.6418 5.83298 10.4678C6.00692 10.2939 6.24283 10.1962 6.48881 10.1962C6.7348 10.1962 6.97071 10.2939 7.14465 10.4678L9.26974 12.5929L15.1027 6.75995C15.1888 6.67383 15.2911 6.60551 15.4036 6.5589C15.5161 6.51229 15.6368 6.4883 15.7585 6.4883C15.8803 6.4883 16.001 6.51229 16.1135 6.5589C16.226 6.60551 16.3283 6.67383 16.4144 6.75995C16.5005 6.84608 16.5688 6.94832 16.6154 7.06085C16.662 7.17338 16.686 7.29399 16.686 7.41579C16.686 7.53759 16.662 7.65819 16.6154 7.77072C16.5688 7.88325 16.5005 7.9855 16.4144 8.07162Z" fill="#6EE7B7" /></svg>
                <span>Todos os seus dados estão seguros e encriptados.</span>
            </div>

            <img src={fxRightImg} className="fx-right-img" alt="decoration" />
            <img src={fxLeftImg} className="fx-left-img" alt="decoration" />
        </React.Fragment>
    )
}

export default Subscription;
